import { Typography, useTheme, Box } from "@mui/material";
import { Col, Row } from "v2/components/base/Flex/ui";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axiosService from "services/axios";
import { useEffect, useState } from "react";
import { transformShillData } from "./utils";
import { ShillDataItem } from "./types";
import ShillScrollableList from "./components/ShillScrollableList";
import { useCheckViewPoint } from "hooks/useCheckViewPoint";

function useCurrentDateTime() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  return now.toLocaleString();
}

const ShillDashboard = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const isMobile = useCheckViewPoint({ to: "md" });

  const {
    data: shillData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["shillData", projectId],
    queryFn: () => (projectId ? axiosService.getShillData({ projectId }) : null),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Transform the data for our new component structure
  const groupedData = shillData?.result
    ? transformShillData(shillData.result as ShillDataItem[])
    : {};

  return (
    <Col
      type="ss"
      sx={{
        position: "relative",
        gap: "16px",
        ...(!isMobile
          ? { maxWidth: "calc(min(1200px, 100vw) - 300px)" }
          : { maxWidth: "calc(100vw - 32px)" }),
      }}
      fullHeight>
      {/* Header */}
      <Row type="sc" sx={{ alignItems: "center", gap: 2 }} fullWidth fullHeight>
        <Box
          sx={{
            borderRadius: "50%",
            width: 60,
            height: 60,
            background: "url(/airdrop-vooi-logo.webp) center center no-repeat",
            backgroundSize: "auto 100%",
          }}
        />
        <Col type="cs" gap="8px" fullHeight>
          <Typography
            variant="mobile-h2"
            sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
            {projectId?.toUpperCase()}
          </Typography>
          <Row gap="8px">
            <Row
              gap="16px"
              sx={{
                p: "8px 16px",
                background: `${theme.palette.surface.bg}`,
                borderRadius: "8px",
              }}>
              <Typography variant="mobile-body-m-heavy" sx={{ fontWeight: 400 }}>
                Shill Earned:{" "}
              </Typography>
              <Typography variant="mobile-body-m" color="brand.primary" sx={{ fontWeight: 400 }}>
                {shillData?.totalShills !== undefined
                  ? shillData.totalShills.toLocaleString(undefined, { maximumFractionDigits: 0 })
                  : "--"}
              </Typography>
            </Row>
            <Row
              gap="16px"
              sx={{
                p: "8px 16px",
                background: `${theme.palette.surface.bg}`,
                borderRadius: "8px",
              }}>
              <Typography variant="mobile-body-m-heavy" sx={{ fontWeight: 400 }}>
                Mentions:{" "}
              </Typography>
              <Typography variant="mobile-body-m" color="brand.primary" sx={{ fontWeight: 400 }}>
                {shillData?.totalMentions !== undefined ? shillData.totalMentions : "--"}
              </Typography>
            </Row>
          </Row>
        </Col>
      </Row>

      {/* Scrollable List Container */}
      <Col fullWidth>
        {/* Render the scrollable list with our grouped data */}
        <ShillScrollableList
          groupedData={groupedData}
          isLoading={isLoading}
          isError={isError}
          error={error}
        />
      </Col>
    </Col>
  );
};

export default ShillDashboard;
